import { onSnapshot, orderBy, query, type QuerySnapshot } from 'firebase/firestore'
import { create } from 'zustand'
import { categoryCollection } from '@/data/categories'
import type { Paths } from '@/data/tasks'
import type { Category, CategoryDoc, Scope } from '@/types'

interface CategoriesState {
  shared: Category[]
  private: Category[]
  /** Shared then private, archived ones included; pickers filter them out. */
  all: Category[]
  byId: Record<string, Category>
  ready: boolean
}

export const useCategories = create<CategoriesState>()(() => ({ shared: [], private: [], all: [], byId: {}, ready: false }))

export function startCategoryListeners(p: Paths): () => void {
  const arrived = { shared: false, private: false }
  const apply = (scope: Scope) => (snap: QuerySnapshot) => {
    const list: Category[] = []
    snap.forEach((d) => list.push({ ...(d.data() as CategoryDoc), id: d.id, scope }))
    arrived[scope] = true
    useCategories.setState((s) => {
      const shared = scope === 'shared' ? list : s.shared
      const priv = scope === 'private' ? list : s.private
      const all = [...shared, ...priv]
      const byId: Record<string, Category> = {}
      for (const c of all) byId[c.id] = c
      return { shared, private: priv, all, byId, ready: arrived.shared && arrived.private }
    })
  }
  const stops = (['shared', 'private'] as Scope[]).map((scope) =>
    onSnapshot(query(categoryCollection(p, scope), orderBy('order')), apply(scope), (err) => {
      console.error(`category listener ${scope}`, err)
      arrived[scope] = true
      useCategories.setState({ ready: arrived.shared && arrived.private })
    }),
  )
  return () => {
    stops.forEach((s) => s())
    useCategories.setState({ shared: [], private: [], all: [], byId: {}, ready: false })
  }
}

/** Categories offered in pickers and chips: live ones, shared first. */
export function activeCategories(all: Category[]): Category[] {
  return all.filter((c) => !c.archived)
}
